import { createContext, useContext } from 'react'
import type { SessionTokens } from '@/lib/auth/tokenStore'

export type AuthState = 'checking' | 'authenticated' | 'unauthenticated'
export type AuthAppRole = 'CLUB_ADMIN' | 'COACH' | 'PLAYER'
export type AuthLoginMode = 'signin' | 'signup' | 'google'
export type AuthLoginOptions = {
  mode?: AuthLoginMode
}

export type AuthMePlayer = {
  player?: {
    playerId?: string
    shortId?: string
    displayName?: string
    email?: string
  } | null
  platform_role?: string
}

export type AuthOrganization = {
  orgId: string
  name?: string
  role?: string
  /** The Coach capability in this org (independent of role) — drives the /profile
   * Coaching tab for manager-coaches AND coach-only members (0c15c53). */
  isCoach?: boolean
  /** Public club slug from GET /organizations (d18f247b) — lets consumers link to
   * the public /clubs/:slug page. Null/undefined until the backend enrich lands. */
  slug?: string | null
}

/**
 * What a password sign-in produced. A correct password can be answered with a
 * challenge instead of a session — NEW_PASSWORD_REQUIRED for an invited account,
 * SOFTWARE_TOKEN_MFA when two-factor is on. Then no tokens were stored, no
 * profile was loaded and nobody is signed in — the caller has to finish the
 * step before any of that is true.
 */
export type AuthPasswordLoginResult =
  | { organizations: AuthOrganization[] }
  | { challenge: string; session: string }

export function isAuthLoginChallenge(
  result: AuthPasswordLoginResult,
): result is { challenge: string; session: string } {
  return 'challenge' in result
}

export type NewPasswordChallengeInput = {
  email: string
  session: string
  password: string
}

export type TwoFactorChallengeInput = {
  email: string
  session: string
  /** Exactly six digits, as the authenticator app shows them. */
  code: string
}

export type AuthContextValue = {
  state: AuthState
  tokens: SessionTokens | null
  me: AuthMePlayer | null
  organizations: AuthOrganization[]
  currentOrgId: string | null
  setCurrentOrgId: (orgId: string | null) => void
  isSuperAdmin: () => boolean
  isClubStaff: (orgId?: string) => boolean
  // Route-level gate for club-staff pages: true when the user is
  // club_staff (or super admin) of ANY org. The per-resource check
  // (per tournament / ladder / club) happens inside the page once
  // it knows the owning orgId. Important on hard refresh, where
  // currentOrgId is hydrated to organizations[0] which may not be
  // the page's target org.
  isAnyClubStaff: () => boolean
  canManageFollowers: (orgId?: string) => boolean
  /** Owner (or super_admin) only: add staff, change roles, remove followers. */
  canEditStaff: (orgId?: string) => boolean
  canManageClub: (orgId?: string) => boolean
  loginWithPassword: (email: string, password: string) => Promise<AuthPasswordLoginResult>
  setPasswordWithToken: (token: string, password: string) => Promise<void>
  /** Finishes an invited sign-in by answering the NEW_PASSWORD_REQUIRED challenge. */
  completeNewPasswordChallenge: (input: NewPasswordChallengeInput) => Promise<AuthOrganization[]>
  /** Finishes a sign-in with two-factor on by answering the SOFTWARE_TOKEN_MFA challenge with a code. */
  completeTwoFactorChallenge: (input: TwoFactorChallengeInput) => Promise<AuthOrganization[]>
  login: (returnTo?: string, options?: AuthLoginOptions) => Promise<void>
  completeCallback: (code: string, state: string | null) => Promise<string>
  logout: () => Promise<void>
  refreshProfile: () => Promise<void>
}

export const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider')
  }
  return context
}
